<template lang="pug">
.bulk-audio-jit-cleanup
  p
    | Remove legacy Tier-2 (pre-recorded S3) audio paths from customisations where the base course has
    |
    code course.audio-ai-jit
    |  enabled for that locale. Customisations with JiT overrides are kept — only redundant S3 paths are removed so base JiT config merges at runtime.

  p.font-bold.text-orange-600.dark_text-orange-400
    | Before you proceed you must ensure that:
    ul
      li base courses have Audio AI JiT applied and published LIVE
      li custom translations have been published LIVE

  p.font-bold.text-red-600.dark_text-red-400(v-if="hasAnyUnsavedChanges")
    | ⚠️ Some selected courses have unsaved changes. Please save or commit all changes before processing.

  p.font-bold.text-yellow-600.dark_text-yellow-400(v-if="basesWithoutJit.length > 0")
    | ⚠️ Base course(s) without JiT config:
    span(v-for="(summary, index) in basesWithoutJit" :key="summary.courseId")
      | {{ index > 0 ? ', ' : ' ' }}{{ summary.courseId }}

  .p-4.my-6.bg-gray-600.dark_bg-gray-900.bg-opacity-10.rounded-lg
    h2.p-0.mt-0 Course Selection

    p Enter base course ID(s) to find customisations with legacy S3 audio paths that can be removed.

    .grid.grid-cols-1.gap-4
      .p-4.border.border-gray-300.dark_border-gray-600.rounded-lg
        .flex.gap-4.items-end
          .flex-1
            label.block.text-sm.font-medium.text-gray-700.dark_text-gray-300.mb-2
              | Base course IDs (comma separated)
            input.w-full.px-3.py-2.border.border-gray-300.dark_border-gray-600.rounded-md.bg-white.dark_bg-gray-800.text-gray-900.dark_text-gray-100(
              type="text"
              placeholder="e.g. 1000, 1001, 1002"
              v-model="courseIdsInput"
              :disabled="loadingCustomisations || isProcessing"
              @keyup.enter="loadCustomisations"
            )

      .p-4.border.border-gray-300.dark_border-gray-600.rounded-lg
        .flex.gap-4.items-end
          button.btn-hover_cursor-pointer.bg-green-600.btn-hover_bg-green-700.disabled_bg-gray-600.disabled_opacity-80.group.text-white.text-center.font-bold.py-2.px-4.rounded(
            type="button"
            :disabled="!courseIdsInput.trim() || loadingCustomisations || isProcessing"
            @click="loadCustomisations"
          )
            span(v-if="!loadingCustomisations") Find Customisations
            span(v-else) Loading...
          button.btn-hover_cursor-pointer.bg-red-600.btn-hover_bg-gray-600.disabled_bg-gray-600.disabled_opacity-80.group.text-white.text-center.font-bold.py-2.px-4.rounded(
            type="button"
            :disabled="isProcessing"
            @click="resetSelection"
          )
            span Reset

        .loading-container(v-if="loadingCustomisations")
          p.text-lg.font-semibold.mb-2 Loading customisations...

        .summary-container(v-else-if="customisationsReports.size > 0")
          p.text-lg.font-semibold(v-if="cleanupSummary.totalCustomisations > 0")
            | Found {{ cleanupSummary.totalBaseCourses }} base course(s) with {{ cleanupSummary.totalCustomisations }} total customisations
            span(v-if="cleanupSummary.withAudio > 0")
              |  ({{ cleanupSummary.withAudio }} with audio)

          .mb-4(v-if="baseJitSummaries.length > 0")
            h3.text-base.font-semibold.mb-2 Base course JiT configuration
            ul.text-sm.list-disc.ms-5
              li(v-for="summary in baseJitSummaries" :key="summary.courseId")
                span.font-mono Course {{ summary.courseId }}:
                span(v-if="summary.error")  {{ summary.error }}
                span(v-else-if="!summary.hasJit")  No JiT configured
                span(v-else)  JiT enabled for {{ summary.jitLocales.join(', ') || 'configured locales' }}

          p.text-sm.text-gray-600.dark_text-gray-400(v-if="checkingUnsavedChanges")
            | Checking for unsaved changes...
          p.text-sm.text-red-600.dark_text-red-400(v-else-if="hasAnyUnsavedChanges")
            | ⚠️ {{ unsavedChangesCount }} selected course(s) have unsaved changes

          .mb-4.flex.gap-8(v-if="cleanupRows.length > 0")
            label.inline-flex.items-center.gap-2.cursor-pointer
              input(
                type="checkbox"
                :disabled="isProcessing"
                @change="onSelectAll"
                :checked="allCoursesSelected(cleanupRows)"
              )
              span.font-medium Select All

            label.inline-flex.items-center.gap-2.cursor-pointer(
              title="Include ALL customisations with a matching base course ID"
            )
              input(type="checkbox" v-model="includeDuplicates")
              span.font-medium Include Duplicates

            label.inline-flex.items-center.gap-2.cursor-pointer(
              title="Also process full-course customisations (with embedded pages), not just patches"
            )
              input(type="checkbox" v-model="includeFullCustomisations" :disabled="isProcessing")
              span.font-medium Include Full Customisations

          .overflow-x-auto.overflow-y-auto.max-h-96(v-if="cleanupRows.length > 0")
            table.w-full.text-sm.m-0.bg-white.dark_bg-gray-800
              thead
                tr.border-b.border-gray-300.dark_border-gray-600
                  th.text-center.align-middle.py-2.px-2 Include
                  th.text-center.align-middle.py-2.px-2 Draft Status
                  th.text-center.align-middle.py-2.px-2 Base Course
                  th.text-center.align-middle.py-2.px-2 Firm Code
                  th.text-center.align-middle.py-2.px-2 Course ID
                  th.text-center.align-middle.py-2.px-2 Type
                  th.text-center.align-middle.py-2.px-2 Title
                  th.text-center.align-middle.py-2.px-2 Last Updated
                  th.text-center.align-middle.py-2.px-2 Tier-2 Locales
                  th.text-center.align-middle.py-2.px-2 JiT Overrides
                  th.text-center.align-middle.py-2.px-2 Locales to Strip
                  th.text-center.align-middle.py-2.px-2 Status
              tbody
                tr.border-b.border-gray-200.dark_border-gray-700(
                  v-for="row in cleanupRows"
                  :key="`${row.firmCode}_${row.courseId}`"
                )
                  td.py-2.px-2.text-center
                    input(
                      type="checkbox"
                      :disabled="isProcessing"
                      @change="toggleCourseSelection(row)"
                      :checked="isCourseSelected(row)"
                    )
                  td.py-2.px-2.text-center
                    .flex.items-center.justify-center.gap-1
                      span.text-sm(v-if="checkingUnsavedChanges") Loading...
                      span.text-sm(v-else-if="getUnsavedChangesStatus(row)?.hasUnsavedChanges")
                        span.text-yellow-600.dark_text-yellow-400.font-bold(:title="getUnsavedChangesTooltip(row)") ⚠️
                      span.text-sm(v-else)
                        span.text-green-600.dark_text-green-400(:title="getUnsavedChangesTooltip(row)") ✓
                      a.text-blue-600.dark_text-blue-400(
                        :href="`#/editor/${row.firmCode}_${row.courseId}`"
                        title="Edit course"
                        target="_blank"
                      ) 📝
                  td.py-2.px-2.font-mono.text-center {{ row.baseCourseId }}
                  td.py-2.px-2.font-mono.text-center.break-words(class="max-w-[10rem]" :title="row.firmCode") {{ row.firmCode }}
                  td.py-2.px-2.font-mono.text-center {{ row.courseId }}
                  td.py-2.px-2.text-center
                    span.text-sm.font-medium.text-orange-600.dark_text-orange-400(
                      v-if="row.isFullCustomisation"
                      title="Full-course customisation (embedded pages)"
                    ) Full
                    span.text-sm.text-gray-600.dark_text-gray-400(
                      v-else
                      title="Patch customisation (overrides only)"
                    ) Patch
                  td.py-2.px-2.text-center {{ row.title }}
                  td.py-2.px-2.text-center.whitespace-nowrap
                    FormatAgo(:date="Number(row.updatedDate) || 0")
                  td.py-2.px-2.text-center
                    span.text-sm(v-if="row.tier2Locales.length") {{ row.tier2Locales.join(', ') }}
                    span.text-gray-500.text-sm(v-else) None
                  td.py-2.px-2.text-center
                    span.text-sm(v-if="row.jitOverrideLocales.length") {{ formatJitOverrides(row.jitOverrideLocales) }}
                    span.text-gray-500.text-sm(v-else) None
                  td.py-2.px-2.text-center
                    span.text-sm.text-orange-600.dark_text-orange-400(v-if="row.strippableLocales.length")
                      | {{ row.strippableLocales.map(s => s.locale).join(', ') }}
                    span.text-gray-500.text-sm(v-else) —
                  td.py-2.px-2.text-center
                    span.text-sm(:class="statusClass(row)")
                      | {{ formatStatus(row) }}

          p.text-gray-600.dark_text-gray-400(v-else-if="cleanupSummary.totalCustomisations > 0")
            | No customisations with audio found for the specified base course IDs.

          p.text-gray-600.dark_text-gray-400(v-else)
            | No customisations found for the specified base course IDs.

  .p-4.my-6.bg-gray-600.dark_bg-gray-900.bg-opacity-10.rounded-lg(
    v-if="cleanupRows.length > 0 && !loadingCustomisations"
  )
    h2.p-0.mt-0 Remove Legacy Audio Paths

    p.text-sm.mb-4
      | Each selected customisation is updated, committed, and published individually. Failures can be retried without re-processing successful rows.

    .flex.gap-4.items-center.mb-4
      span Choose destination:
      label.inline-flex.gap-2.items-center
        input(type="radio" v-model="publishDestination" value="test" :disabled="isProcessing")
        span Internal
      label.inline-flex.gap-2.items-center
        input(type="radio" v-model="publishDestination" value="production" :disabled="isProcessing")
        span Live

    .flex.flex-col.gap-4.items-center
      div.w-full.text-center.py-2.px-4.rounded(v-if="!isProcessing && processingCompleted")
        span.text-green-600.dark_text-green-400.text-2xl.font-bold ✓ Success

      template(v-else)
        button.w-full.btn-hover_cursor-pointer.bg-green-600.btn-hover_bg-green-700.disabled_bg-gray-600.disabled_opacity-80.group.text-white.text-center.font-bold.py-2.px-4.rounded(
          type="button"
          :disabled="selectedStrippableCount === 0 || isProcessing || hasAnyUnsavedChanges"
          @click="startCleanup"
        )
          span(v-if="!isProcessing && !hasAnyUnsavedChanges") Remove Paths & Publish ({{ selectedStrippableCount }})
          span(v-else-if="!isProcessing && hasAnyUnsavedChanges") Unsaved Changes Detected
          span(v-else) Processing...

        button.w-full.btn-hover_cursor-pointer.bg-orange-600.btn-hover_bg-orange-700.disabled_bg-gray-600.disabled_opacity-80.group.text-white.text-center.font-bold.py-2.px-4.rounded(
          type="button"
          :disabled="failedRows.length === 0 || isProcessing"
          @click="retryFailed"
          v-if="failedRows.length > 0"
        )
          span Retry Failed ({{ failedRows.length }})

        button.w-full.btn-hover_cursor-pointer.bg-red-600.btn-hover_bg-red-700.disabled_bg-gray-600.disabled_opacity-80.group.text-white.text-center.font-bold.py-2.px-4.rounded(
          type="button"
          :disabled="!isProcessing"
          @click="stopProcessing"
          v-if="isProcessing"
        )
          span Stop Processing

      .text-sm.text-gray-600.dark_text-gray-400(v-if="isProcessing")
        p Processing: {{ completedCount }}/{{ totalCount }} completed
        p(v-if="currentProcessing") Currently processing: {{ currentProcessing }}
</template>

<script setup lang="ts">
import FormatAgo from './FormatAgo.vue';
import { updateHashUrl } from '../lib/hashParams';
import {
  useJitAudioCleanup,
  type CleanupRow
} from '../composables/useJitAudioCleanup';

const {
  courseIdsInput,
  loadingCustomisations,
  customisationsReports,
  includeDuplicates,
  includeFullCustomisations,
  checkingUnsavedChanges,
  cleanupSummary,
  cleanupRows,
  baseJitSummaries,
  basesWithoutJit,
  selectedStrippableCount,
  hasAnyUnsavedChanges,
  unsavedChangesCount,
  failedRows,
  processingCompleted,
  isProcessing,
  currentProcessing,
  completedCount,
  totalCount,
  publishDestination,
  loadCustomisations,
  resetProcessingState,
  startCleanup,
  retryFailed,
  stopProcessing,
  isCourseSelected,
  selectAllCourses,
  toggleCourseSelection,
  allCoursesSelected,
  getUnsavedChangesStatus,
  getUnsavedChangesTooltip
} = useJitAudioCleanup();

const resetSelection = () => {
  courseIdsInput.value = '';
  resetProcessingState();
  updateHashUrl('/bulk-audio', new URLSearchParams());
};

const onSelectAll = (event: Event) => {
  selectAllCourses(event, cleanupRows.value);
};

const formatJitOverrides = (locales: string[]) => {
  if (locales.includes('*')) return 'Global';
  return locales.join(', ');
};

const formatStatus = (row: CleanupRow) => {
  if (row.status === 'processing') return 'Processing';
  if (row.status === 'success') return 'Complete';
  if (row.status === 'error') return row.error || 'Failed';
  if (row.status === 'skipped') {
    return row.strippableLocales.length === 0 ? 'Nothing to strip' : 'Skipped';
  }
  return 'Pending';
};

const statusClass = (row: CleanupRow) => {
  if (row.status === 'processing')
    return 'text-yellow-600 dark_text-yellow-400';
  if (row.status === 'success') return 'text-green-600 dark_text-green-400';
  if (row.status === 'error') return 'text-red-600 dark_text-red-400';
  if (row.status === 'skipped') return 'text-gray-500';
  return 'text-gray-600 dark_text-gray-400';
};
</script>
